import Glide, { Controls, Swipe, Breakpoints, Autoplay } from '@glidejs/glide/dist/glide.modular.esm'
import { clickAndDrag } from './click-and-drag';

function sliderMain() {
    const sliders = document.querySelectorAll('.slider-main');
    if (!sliders.length) return;

    function updateProgressRing(bullets, progress) {
        bullets.forEach(bullet => {
            if (bullet.classList.contains('glide__bullet--active')) {
                const bulletCircle = bullet.querySelector('.percent');
                if (bulletCircle)
                    bulletCircle.style.strokeDashoffset = 100 - progress;
            }
        });
    }

    function manageTabIndexes(slider, glide) {
        const realSlides = [...slider.querySelectorAll('.glide__slide:not(.glide__slide--clone)')];
        const bullets = [...slider.querySelectorAll('.glide__bullet')];
        let userInteracted = false;

        function updateTabIndexes() {
            let lastFocusedElement = document.activeElement;
            let focusedIndex = realSlides.findIndex(slide => slide.contains(lastFocusedElement));
            
            realSlides.forEach((slide, index) => {
                const btn = slide.querySelector('.btn');
                if (btn) {
                    btn.setAttribute('tabindex', index === glide.index ? '0' : '-1');
                    if (userInteracted && index === glide.index && focusedIndex === -1) {
                        btn.focus();
                        userInteracted = false;
                    }
                }
            });

            if (glide.index === realSlides.length - 1) {
                bullets.forEach(bullet => bullet.setAttribute('tabindex', '-1'));
            } else {
                bullets.forEach((bullet, index) => {
                    bullet.setAttribute('tabindex', index > glide.index ? '0' : '-1');
                });
            }
        }

        bullets.forEach(bullet => {
            bullet.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    userInteracted = true;
                }
            });
        });

        glide.on('run.after', updateTabIndexes);
        updateTabIndexes();
    }

    function manageAutoplay(glideInstance, slider) {
        const progressDuration = +slider.dataset.autoplay ?? 0;
        if (!progressDuration) return;
        let autoplayTimer;
        let startTime = Date.now();

        const startAutoplayTimer = () => {
            startTime = Date.now();
            autoplayTimer = setInterval(() => {
                const currentTime = Date.now();
                const elapsedTime = currentTime - startTime;
                const progress = Math.min(100, (elapsedTime / progressDuration) * 100);
                const bullets = document.querySelectorAll('.glide__bullet');
                updateProgressRing(bullets, progress);

                if (elapsedTime >= progressDuration) {
                    glideInstance.go('>');
                    startTime = Date.now();
                }
            }, 10);
        };

        const resetAutoplayTimer = () => {
            clearInterval(autoplayTimer);
            startAutoplayTimer();
        };

        glideInstance.on('run.after', resetAutoplayTimer);
        glideInstance.on('play', startAutoplayTimer);
        glideInstance.on('run.before', () => clearInterval(autoplayTimer));

        startAutoplayTimer();
    }

    sliders.forEach(slider => {
        const glide = new Glide(slider, {
            type: 'carousel',
            swipeThreshold: 20,
            gap: 0,
            animationDuration: 1000
        }).mount({ Controls, Swipe });

        function updatePagination() {
            const bullets = document.querySelectorAll('.glide__bullet');
            bullets.forEach((bullet, index) => {
                (index === glide.index)
                    ? bullet.classList.add('active')
                    : bullet.classList.remove('active');
            });
        }

        glide.on('run.after', () => {
            updatePagination();
        });

        updatePagination();
        manageAutoplay(glide, slider);
        manageTabIndexes(slider, glide);

        const videoPlayButtons = slider.querySelectorAll('.video-play-btn');
        if (!videoPlayButtons.length) return;

        videoPlayButtons.forEach(button => {
            const video = button.previousElementSibling;

            button.addEventListener('click', () => video.paused ? video.play() : video.pause());

            video.addEventListener('play', () => button.classList.add('playing'));
            video.addEventListener('pause', () => button.classList.remove('playing'));
        });
    });
}

function sliderPagination() {
    const sliders = document.querySelectorAll('[data-pagination-slider]');
    if (!sliders.length) return;
    
    sliders.forEach(slider => {
        let intervalID;
        let startX = 0;
        let isDragging = false;
    
        function switchSlide(direction, index) {
            if (intervalID) {
                clearInterval(intervalID);
                startAutoplay();
            }

            const activeSlide = slider.querySelector('.active');
            activeSlide.classList.remove('active');
            
            if (index !== undefined) {
                slides[index].classList.add('active');
            } else {
                const prevSlide = activeSlide.previousElementSibling ?? slides[slides.length - 1];
                const nextSlide = activeSlide.nextElementSibling ?? slides[0];

                if (direction === 'previous') {
                    prevSlide.classList.add('active');
                } else {
                    nextSlide.classList.add('active');
                }
            }
        }

        function startAutoplay() {
            intervalID = setInterval(() => {
                switchSlide('next');
            }, autoplayMs);
        }

        const slides = Array.from(slider.querySelectorAll('[data-pagination-slide]'));

        slides.forEach(slide => {
            const prevBtn = slide.querySelector('[data-pagination-prev]');
            const nextBtn = slide.querySelector('[data-pagination-next]');
    
            prevBtn?.addEventListener('click', () => switchSlide('previous'));
            nextBtn?.addEventListener('click', () => switchSlide('next'));
        });

        const autoplayMs = slider.dataset.autoplay;

        if (autoplayMs) {
            startAutoplay();
            window.onbeforeunload = () => clearInterval(intervalID);
        }
    
        function onTouchStart(e) {
            startX = e.touches[0].clientX;
            isDragging = true;
        }
    
        function onTouchMove(e) {
            if (!isDragging) return;
            let currentX = e.touches[0].clientX;
            let diff = startX - currentX;
            if (Math.abs(diff) > 50) {
                switchSlide(diff > 0 ? 'next' : 'previous');
                isDragging = false;
            }
        }
    
        function onTouchEnd() {
            isDragging = false;
        }

        slider.addEventListener('touchstart', onTouchStart);
        slider.addEventListener('touchmove', onTouchMove);
        slider.addEventListener('touchend', onTouchEnd);
    
        if (slider.classList.contains('slider-cta--modal')) {
            const modalId = slider.parentElement.id;
            const sliderModalBtns = document.querySelector(`.${modalId}`)?.querySelectorAll('.open-slider');
            sliderModalBtns?.forEach((btn, index) => {
                btn.addEventListener('click', () => switchSlide(null, index));
            });
        }
    });
}

function dragSliders() {
    const dragSliders = document.querySelectorAll('.drag-wrapper');
    if (!dragSliders.length) return;

    dragSliders.forEach(slider => {
        const dragAPI = clickAndDrag(slider);
        slider.dragAPI = dragAPI;
    });
}

    
function sliderStory() {
    const stories = document.querySelectorAll('.story');
    if (!stories.length) return;

    stories.forEach(story => {
        let activeIndex = 0;

        const scrollableLine = story.querySelector('.story__line');
        const line = story.querySelector('.line');
        const storyElements = Array.from(story.querySelectorAll('.story__element'));
        const sortedElements = storyElements.sort((a, b) => {
            const dateA = a.getAttribute('data-date');
            const dateB = b.getAttribute('data-date');
            return new Date(dateB) - new Date(dateA);
        });

        toggleElement(sortedElements[0], 0);

        storyElements.forEach((el, index) => {
            el.addEventListener('pointerover', () => toggleElement(el, index));
        })

        function toggleElement(element, index) {
            storyElements[activeIndex].classList.remove('active');
            storyElements[index].classList.add('active');
            activeIndex = index;

            const previousElements = sortedElements.slice(0, index);
            const nextElements = sortedElements.slice(index + 1);
            setCircleColor(previousElements, 'var(--green-1)');
            setCircleColor(nextElements, '');

            const elementRect = element.getBoundingClientRect();
            const scrollableRect = scrollableLine.getBoundingClientRect();
            const offsetToElement = elementRect.left - scrollableRect.left + elementRect.width / 2;
            line.style.width = `${offsetToElement}px`;
        }

        function setCircleColor(elements, color) {
            elements.forEach(el => el.style.setProperty('--circle-color', color));
        }
    });
}

export function initSliders() {
    sliderMain()
    sliderPagination()
    dragSliders()
    sliderStory()
}

export function initSliderProductsFlaggedSlide(scope)
{
    sliderProductsFlagged(scope)
}
